"use client";

import { useState } from "react";
import { useCartActions } from "@/lib/store/cart-store-provider";
import {
  generateCartItemId,
  type SelectedOption,
} from "@/lib/store/cart-store";
import { formatCurrencyEUR } from "@/lib/i18n";
import { CheckIcon } from "lucide-react";

interface CustomizationOption {
  value: string;
  name: string;
  colorHex?: string | null;
  priceModifier?: number | null;
}

interface CustomizationGroup {
  groupId: string;
  name: string;
  required?: boolean | null;
  displayType?: string | null;
  options: CustomizationOption[];
}

interface ConfigurableProduct {
  _id: string;
  name: string;
  price: number;
  images?: { _key?: string | null; asset?: { url?: string | null } | null }[];
  customizationGroups?: CustomizationGroup[] | null;
  stock?: number | null;
}

interface ProductConfiguratorProps {
  product: ConfigurableProduct;
  locale: "en-US" | "pt-PT";
  copy?: {
    addToCart?: string;
    addedToCart?: string;
    outOfStock?: string;
    selectRequired?: string;
    priceFrom?: string;
    optional?: string;
    quantity?: string;
  };
}

const defaultCopy = {
  "en-US": {
    addToCart: "Add to Cart",
    addedToCart: "Added!",
    outOfStock: "Out of Stock",
    selectRequired: "Please select all required options",
    priceFrom: "from",
    optional: "(optional)",
    quantity: "Quantity",
  },
  "pt-PT": {
    addToCart: "Adicionar ao Carrinho",
    addedToCart: "Adicionado!",
    outOfStock: "Sem Stock",
    selectRequired: "Por favor escolha todas as opções obrigatórias",
    priceFrom: "a partir de",
    optional: "(opcional)",
    quantity: "Quantidade",
  },
} as const;

export function ProductConfigurator({
  product,
  locale,
  copy: copyOverrides,
}: ProductConfiguratorProps) {
  const { addItem } = useCartActions();
  const t = { ...defaultCopy[locale], ...copyOverrides };

  const groups = product.customizationGroups ?? [];
  const [selections, setSelections] = useState<Record<string, SelectedOption>>({});
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  const isOutOfStock = (product.stock ?? 0) <= 0;
  const maxQty = product.stock ?? 99;

  const totalModifier = Object.values(selections).reduce(
    (sum, o) => sum + o.priceModifier,
    0,
  );
  const finalPrice = product.price + totalModifier;

  const requiredGroups = groups.filter((g) => g.required !== false);
  const allRequiredSelected = requiredGroups.every((g) => !!selections[g.groupId]);

  function handleSelect(group: CustomizationGroup, option: CustomizationOption) {
    setSelections((prev) => ({
      ...prev,
      [group.groupId]: {
        groupId: group.groupId,
        groupName: group.name,
        optionValue: option.value,
        optionName: option.name,
        priceModifier: option.priceModifier ?? 0,
      },
    }));
  }

  function handleAddToCart() {
    if (!allRequiredSelected || isOutOfStock) return;

    const selectedOptionsList = Object.values(selections);
    const cartItemId = generateCartItemId(product._id, selectedOptionsList);
    const imageUrl = product.images?.[0]?.asset?.url ?? undefined;

    addItem(
      {
        cartItemId,
        productId: product._id,
        name: product.name,
        basePrice: product.price,
        price: finalPrice,
        image: imageUrl,
        selectedOptions: selectedOptionsList,
      },
      quantity,
    );

    setAdded(true);
    setQuantity(1);
    setTimeout(() => setAdded(false), 2000);
  }

  return (
    <div className="space-y-6">
      {/* Customization groups */}
      {groups.length > 0 && (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.groupId}>
              <div className="mb-2.5 flex items-center gap-2">
                <p className="text-sm font-medium tracking-[0.08em] text-[#2B2824] uppercase">
                  {group.name}
                </p>
                {group.required === false && (
                  <span className="text-xs text-[#8E7F6D]">{t.optional}</span>
                )}
              </div>

              {group.displayType === "swatches" ? (
                <div className="flex flex-wrap gap-2.5">
                  {group.options.map((option) => {
                    const isSelected = selections[group.groupId]?.optionValue === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleSelect(group, option)}
                        title={option.name}
                        className={`relative h-9 w-9 rounded-full border-2 transition-all ${
                          isSelected
                            ? "border-[#4F4A43] shadow-md scale-110"
                            : "border-[#D9D1C6] hover:border-[#8E7F6D]"
                        }`}
                        style={{ backgroundColor: option.colorHex ?? "#ccc" }}
                        aria-label={option.name}
                        aria-pressed={isSelected}
                      >
                        {isSelected && (
                          <span className="absolute inset-0 flex items-center justify-center">
                            <CheckIcon
                              className="h-4 w-4 drop-shadow"
                              style={{
                                color: isLightColor(option.colorHex ?? "#ccc")
                                  ? "#2B2824"
                                  : "#FBFAF7",
                              }}
                            />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {group.options.map((option) => {
                    const isSelected = selections[group.groupId]?.optionValue === option.value;
                    const modifier = option.priceModifier ?? 0;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleSelect(group, option)}
                        aria-pressed={isSelected}
                        className={`rounded-full border px-4 py-2 text-sm transition-all ${
                          isSelected
                            ? "border-[#4F4A43] bg-[#4F4A43] text-[#F8F6F1]"
                            : "border-[#D9D1C6] bg-[#FBFAF7] text-[#2B2824] hover:bg-[#F0EBE3]"
                        }`}
                      >
                        {option.name}
                        {modifier !== 0 && (
                          <span
                            className={`ml-1.5 text-xs ${isSelected ? "text-[#D9D1C6]" : "text-[#8E7F6D]"}`}
                          >
                            {modifier > 0 ? "+" : ""}
                            {formatCurrencyEUR(modifier, locale)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Price + quantity + add to cart */}
      <div className="border-t border-[#E3DDD3] pt-5 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            {totalModifier !== 0 && (
              <p className="text-xs text-[#8E7F6D]">
                {t.priceFrom} {formatCurrencyEUR(product.price, locale)}
              </p>
            )}
            <p className="text-2xl font-medium text-[#2B2824]">
              {formatCurrencyEUR(finalPrice * quantity, locale)}
            </p>
            {quantity > 1 && (
              <p className="text-xs text-[#8E7F6D]">
                {quantity} × {formatCurrencyEUR(finalPrice, locale)}
              </p>
            )}
          </div>

          {!isOutOfStock && (
            <div className="flex flex-col items-end gap-1">
              <p className="text-xs text-[#8E7F6D]">{t.quantity}</p>
              <div className="flex items-center gap-1 rounded-full border border-[#D9D1C6] px-2 py-1">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-base text-[#2B2824] transition hover:bg-[#F0EBE3] disabled:opacity-30"
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span className="min-w-[2ch] text-center text-sm font-medium text-[#2B2824]">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                  disabled={quantity >= maxQty}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-base text-[#2B2824] transition hover:bg-[#F0EBE3] disabled:opacity-30"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleAddToCart}
          disabled={isOutOfStock || (!allRequiredSelected && groups.length > 0)}
          className="w-full rounded-full bg-[#4F4A43] px-6 py-3 text-sm font-medium text-[#F8F6F1] transition hover:bg-[#666058] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isOutOfStock ? t.outOfStock : added ? t.addedToCart : t.addToCart}
        </button>
      </div>

      {groups.length > 0 && !allRequiredSelected && (
        <p className="text-xs text-[#8E7F6D]">{t.selectRequired}</p>
      )}
    </div>
  );
}

function isLightColor(hex: string): boolean {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
}
